import React, { useState, useEffect } from 'react';
import { ShoppingCart, Plus, Minus, Trash2, Package } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import './Compras.css';

const Compras = ({ onPurchaseComplete, defaultCreditMode = false }) => {
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCheckoutModal, setShowCheckoutModal] = useState(false);
    const [checkoutForm, setCheckoutForm] = useState({
        payment_method: 'efectivo',
        payment_status: 'paid',  // Siempre pagado
        notes: ''
    });

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/admin/inventory`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                // Solo mostrar productos activos con stock disponible
                const availableProducts = data.filter(p => p.active && p.stock > 0);
                setProducts(availableProducts);
            }
        } catch (error) {
            console.error('Error loading products:', error);
        } finally {
            setLoading(false);
        }
    };

    const addToCart = (product) => {
        const existingItem = cart.find(item => item.id === product.id);
        if (existingItem) {
            if (existingItem.quantity < product.stock) {
                setCart(cart.map(item =>
                    item.id === product.id
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                ));
            } else {
                alert(`No hay suficiente stock. Stock disponible: ${product.stock}`);
            }
        } else {
            setCart([...cart, { ...product, quantity: 1 }]);
        }
    };

    const removeFromCart = (productId) => {
        setCart(cart.filter(item => item.id !== productId));
    };

    const updateQuantity = (productId, newQuantity) => {
        if (newQuantity <= 0) {
            removeFromCart(productId);
            return;
        }
        const product = products.find(p => p.id === productId);
        if (newQuantity > product.stock) {
            alert(`No hay suficiente stock. Stock disponible: ${product.stock}`);
            return;
        }
        setCart(cart.map(item =>
            item.id === productId
                ? { ...item, quantity: newQuantity }
                : item
        ));
    };

    const getTotal = () => {
        return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    };

    const handleCheckout = async () => {
        if (cart.length === 0) {
            alert('El carrito está vacío');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            
            // Crear la compra y actualizar stock
            const response = await fetch(`${API_BASE_URL}/admin/purchases`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    items: cart.map(item => ({
                        product_id: item.id,
                        quantity: item.quantity,
                        price: item.price
                    })),
                    payment_method: checkoutForm.payment_method,
                    payment_status: 'paid',  // Siempre pagado
                    notes: checkoutForm.notes,
                    total: getTotal()
                })
            });

            if (response.ok) {
                alert('Compra realizada exitosamente');
                setCart([]);
                setShowCheckoutModal(false);
                setCheckoutForm({ payment_method: 'efectivo', payment_status: 'paid', notes: '' });
                // Recargar productos para actualizar stock
                loadProducts();
                // Notificar al componente padre para recargar estadísticas
                if (onPurchaseComplete) {
                    onPurchaseComplete();
                }
            } else {
                const errorData = await response.json();
                alert(errorData.detail || 'Error al realizar la compra');
            }
        } catch (error) {
            console.error('Error processing purchase:', error);
            alert('Error al conectar con el servidor');
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(value);
    };

    const getCategoryLabel = (category) => {
        const labels = {
            'bebidas': 'Bebidas',
            'productos_capilares': 'Productos Capilares',
            'otros': 'Otros'
        };
        return labels[category] || category;
    };

    const getStockStatus = (stock) => {
        if (stock <= 0) return { class: 'stock-out', text: 'Sin Stock' };
        if (stock <= 5) return { class: 'stock-low', text: 'Stock Bajo' };
        return { class: 'stock-ok', text: 'Disponible' };
    };

    const productsByCategory = products.reduce((acc, product) => {
        if (!acc[product.category]) acc[product.category] = [];
        acc[product.category].push(product);
        return acc;
    }, {});

    return (
        <div>
            <div className="compras-header">
                <div>
                    <h2>Realizar Compras</h2>
                    <p className="compras-subtitle">Selecciona productos del inventario para vender</p>
                </div>
                {cart.length > 0 && (
                    <button 
                        className="checkout-btn"
                        onClick={() => setShowCheckoutModal(true)}
                    >
                        <ShoppingCart size={20} />
                        Finalizar Compra ({cart.length})
                    </button>
                )}
            </div>

            <div className="compras-content">
                <div className="products-section">
                    {loading ? (
                        <div className="loading-container">
                            <div className="loading-spinner" />
                        </div>
                    ) : products.length === 0 ? (
                        <div className="empty-state">
                            <Package size={48} />
                            <p>No hay productos disponibles</p>
                        </div>
                    ) : (
                        Object.keys(productsByCategory).map(category => (
                            <div key={category} className="category-section">
                                <h3 className="category-title">{getCategoryLabel(category)}</h3>
                                <div className="products-grid">
                                    {productsByCategory[category].map(product => {
                                        const stockStatus = getStockStatus(product.stock);
                                        const cartItem = cart.find(item => item.id === product.id);
                                        return (
                                            <div key={product.id} className="product-card">
                                                {product.image_url && (
                                                    <div className="product-image">
                                                        <img src={product.image_url} alt={product.name} />
                                                    </div>
                                                )}
                                                <div className="product-body">
                                                    <h4>{product.name}</h4>
                                                    {product.description && (
                                                        <p className="product-description">{product.description}</p>
                                                    )}
                                                    <div className="product-info">
                                                        <div className="product-price">
                                                            {formatCurrency(product.price)}
                                                        </div>
                                                        <div className={`stock-badge ${stockStatus.class}`}>
                                                            Stock: {product.stock}
                                                        </div>
                                                    </div>
                                                    {cartItem ? (
                                                        <div className="cart-controls">
                                                            <button
                                                                onClick={() => updateQuantity(product.id, cartItem.quantity - 1)}
                                                                className="quantity-btn"
                                                            >
                                                                <Minus size={16} />
                                                            </button>
                                                            <span className="quantity-display">{cartItem.quantity}</span>
                                                            <button
                                                                onClick={() => updateQuantity(product.id, cartItem.quantity + 1)}
                                                                className="quantity-btn"
                                                                disabled={cartItem.quantity >= product.stock}
                                                            >
                                                                <Plus size={16} />
                                                            </button>
                                                            <button
                                                                onClick={() => removeFromCart(product.id)}
                                                                className="remove-btn"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => addToCart(product)}
                                                            className="add-to-cart-btn"
                                                            disabled={product.stock <= 0}
                                                        >
                                                            <Plus size={16} />
                                                            Agregar
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {cart.length > 0 && (
                    <div className="cart-sidebar">
                        <div className="cart-header">
                            <h3>Carrito de Compras</h3>
                            <span className="cart-count">{cart.length} {cart.length === 1 ? 'producto' : 'productos'}</span>
                        </div>
                        <div className="cart-items">
                            {cart.map(item => (
                                <div key={item.id} className="cart-item">
                                    <div className="cart-item-info">
                                        <h4>{item.name}</h4>
                                        <p>{formatCurrency(item.price)} x {item.quantity}</p>
                                    </div>
                                    <div className="cart-item-total">
                                        {formatCurrency(item.price * item.quantity)}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="cart-footer">
                            <div className="cart-total">
                                <span>Total:</span>
                                <span className="total-amount">{formatCurrency(getTotal())}</span>
                            </div>
                            <button
                                className="checkout-btn-full"
                                onClick={() => setShowCheckoutModal(true)}
                            >
                                Finalizar Compra
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {showCheckoutModal && (
                <div className="modal-overlay" onClick={() => setShowCheckoutModal(false)}>
                    <div className="modal compras-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <div className="compras-modal-header" style={{ 
                            backgroundColor: '#ef4444', 
                            color: 'white', 
                            padding: '1.5rem', 
                            borderRadius: '8px 8px 0 0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h2 style={{ margin: 0 }}>Finalizar Compra</h2>
                            <button 
                                onClick={() => setShowCheckoutModal(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div className="compras-modal-content" style={{ padding: '2rem' }}>
                            <div className="checkout-summary">
                                <h3>Resumen de Compra</h3>
                                {cart.map(item => (
                                    <div key={item.id} className="checkout-item">
                                        <span>{item.name} x {item.quantity}</span>
                                        <span>{formatCurrency(item.price * item.quantity)}</span>
                                    </div>
                                ))}
                                <div className="checkout-total">
                                    <span>Total:</span>
                                    <span>{formatCurrency(getTotal())}</span>
                                </div>
                            </div>
                            <div className="checkout-form-field" style={{ marginTop: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                    Método de Pago *
                                </label>
                                <select
                                    value={checkoutForm.payment_method}
                                    onChange={(e) => setCheckoutForm({ ...checkoutForm, payment_method: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '1rem'
                                    }}
                                >
                                    <option value="efectivo">Efectivo</option>
                                    <option value="nequi">Nequi</option>
                                    <option value="bancolombia">Bancolombia</option>
                                    <option value="daviplata">Daviplata</option>
                                </select>
                            </div>
                            <div className="checkout-form-field" style={{ marginTop: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                    Notas (opcional)
                                </label>
                                <textarea
                                    value={checkoutForm.notes}
                                    onChange={(e) => setCheckoutForm({ ...checkoutForm, notes: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '1rem',
                                        minHeight: '80px',
                                        resize: 'vertical'
                                    }}
                                    placeholder="Notas adicionales sobre la compra..."
                                />
                            </div>
                            <div className="compras-modal-actions" style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                                <button
                                    onClick={() => setShowCheckoutModal(false)}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        backgroundColor: 'white',
                                        color: '#374151',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleCheckout}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem',
                                        border: 'none',
                                        borderRadius: '6px',
                                        backgroundColor: '#ef4444',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    Confirmar Compra
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Compras;

